import { Head, router } from "@inertiajs/react";
import { CreditCard, Globe2, Image as ImageIcon, Timer, Users } from "lucide-react";
import { type FormEvent, useMemo, useState } from "react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";

interface SummaryStats {
  totalUsers: number;
  activeTrials: number;
  subscribers: number;
  totalDomains: number;
  totalImages: number;
}

type TrialStatus = "active" | "expired" | "none";
type UserSource = "web" | "wordpress";

interface AdminUser {
  id: number;
  name: string;
  email: string;
  source: UserSource;
  createdAt: string;
  subscriptionPlan?: string | null;
  subscriptionStatus?: string | null;
  trialStatus: TrialStatus;
  trialEndsAt?: string | null;
  domainsCount: number;
  domains: string[];
  imagesCount: number;
}

interface AdminUsersPageProps {
  summary: SummaryStats;
  users: AdminUser[];
}

const numberFormatter = new Intl.NumberFormat();
const dateFormatter = new Intl.DateTimeFormat(undefined, {
  dateStyle: "medium",
  timeStyle: "short",
});
const dateOnlyFormatter = new Intl.DateTimeFormat(undefined, {
  dateStyle: "medium",
});

function addDays(date: Date, days: number) {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
}

function formatDateInputValue(date: Date) {
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, "0");
  const day = `${date.getDate()}`.padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function suggestedTrialDate(user: AdminUser, minimumDate: string) {
  if (!user.trialEndsAt) return minimumDate;
  const parsed = new Date(user.trialEndsAt);
  if (Number.isNaN(parsed.getTime())) return minimumDate;
  const formatted = formatDateInputValue(parsed);
  return formatted < minimumDate ? minimumDate : formatted;
}

function formatPlanName(plan?: string | null) {
  if (!plan) return "—";
  return plan.replace(/_/g, " ").replace(/\b\w/g, (char) => char.toUpperCase());
}

function trialBadgeVariant(status: TrialStatus) {
  switch (status) {
    case "active":
      return { label: "Trialing", variant: "success" as const };
    case "expired":
      return { label: "Trial ended", variant: "secondary" as const };
    default:
      return { label: "No trial", variant: "outline" as const };
  }
}

function subscriptionBadgeVariant(status?: string | null) {
  if (!status) return { label: "Unknown", variant: "outline" as const };

  const normalized = status.replace(/_/g, " ");
  if (status === "active") return { label: "Active", variant: "success" as const };
  if (status === "past_due") return { label: "Past due", variant: "secondary" as const };
  if (status === "canceled" || status === "incomplete_expired" || status === "unpaid") {
    return { label: normalized, variant: "destructive" as const };
  }

  return { label: normalized, variant: "outline" as const };
}

function sourceBadgeVariant(source: UserSource) {
  if (source === "wordpress") {
    return { label: "Wordpress", variant: "secondary" as const };
  }

  return { label: "web", variant: "outline" as const };
}

export default function AdminUsersPage({ summary, users }: AdminUsersPageProps) {
  const minimumTrialDate = useMemo(() => formatDateInputValue(addDays(new Date(), 1)), []);
  const minimumTrialDateLabel = useMemo(() => dateOnlyFormatter.format(addDays(new Date(), 1)), []);
  const [trialDialogOpen, setTrialDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<AdminUser | null>(null);
  const [trialEndDate, setTrialEndDate] = useState("");
  const [isSubmittingTrial, setIsSubmittingTrial] = useState(false);
  const [deletingUserId, setDeletingUserId] = useState<number | null>(null);

  const handleViewSocialImages = (userId: number) => {
    router.visit(`/admin/users/${userId}/social_images`);
  };

  const openTrialDialog = (user: AdminUser) => {
    setSelectedUser(user);
    setTrialEndDate(suggestedTrialDate(user, minimumTrialDate));
    setTrialDialogOpen(true);
  };

  const closeTrialDialog = () => {
    setTrialDialogOpen(false);
    setSelectedUser(null);
    setTrialEndDate("");
    setIsSubmittingTrial(false);
  };

  const handleTrialDialogChange = (open: boolean) => {
    if (open) {
      setTrialDialogOpen(true);
      return;
    }
    closeTrialDialog();
  };

  const handleExtendTrial = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!selectedUser || !trialEndDate || trialEndDate < minimumTrialDate) return;

    router.post(
      `/admin/users/${selectedUser.id}/extend_trial`,
      { trial_ends_at: trialEndDate },
      {
        preserveScroll: true,
        onStart: () => setIsSubmittingTrial(true),
        onFinish: () => setIsSubmittingTrial(false),
        onSuccess: () => closeTrialDialog(),
      },
    );
  };

  const handleDeleteUser = (user: AdminUser) => {
    const confirmed = window.confirm(`Delete user ${user.email}? This cannot be undone.`);
    if (!confirmed) return;

    router.delete(`/admin/users/${user.id}`, {
      preserveScroll: true,
      onStart: () => setDeletingUserId(user.id),
      onFinish: () =>
        setDeletingUserId((currentDeletingUserId) =>
          currentDeletingUserId === user.id ? null : currentDeletingUserId,
        ),
    });
  };

  return (
    <>
      <Head title="Admin · Users" />

      <div className="container mx-auto max-w-7xl space-y-10 px-6 py-10">
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div>
            <h1 className="text-3xl font-semibold text-slate-900 dark:text-slate-50">User accounts</h1>
            <p className="mt-1 text-slate-600 dark:text-slate-400">
              Signed up users with their trials, plans, domains, and image activity.
            </p>
          </div>
        </div>

        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium text-slate-500 dark:text-slate-400">Total users</CardTitle>
              <Users className="h-5 w-5 text-slate-400 dark:text-slate-500" />
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-semibold text-slate-900 dark:text-slate-50">
                {numberFormatter.format(summary.totalUsers)}
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium text-slate-500 dark:text-slate-400">Active trials</CardTitle>
              <Timer className="h-5 w-5 text-slate-400 dark:text-slate-500" />
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-semibold text-slate-900 dark:text-slate-50">
                {numberFormatter.format(summary.activeTrials)}
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium text-slate-500 dark:text-slate-400">Subscribers</CardTitle>
              <CreditCard className="h-5 w-5 text-slate-400 dark:text-slate-500" />
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-semibold text-slate-900 dark:text-slate-50">
                {numberFormatter.format(summary.subscribers)}
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium text-slate-500 dark:text-slate-400">Domains tracked</CardTitle>
              <Globe2 className="h-5 w-5 text-slate-400 dark:text-slate-500" />
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-semibold text-slate-900 dark:text-slate-50">
                {numberFormatter.format(summary.totalDomains)}
              </p>
            </CardContent>
          </Card>

          <Card className="md:col-span-2 lg:col-span-4">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium text-slate-500 dark:text-slate-400">Images generated</CardTitle>
              <ImageIcon className="h-5 w-5 text-slate-400 dark:text-slate-500" />
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-semibold text-slate-900 dark:text-slate-50">
                {numberFormatter.format(summary.totalImages)}
              </p>
            </CardContent>
          </Card>
        </div>

        <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
          <div className="flex items-center justify-between border-b border-slate-100 px-6 py-4 dark:border-slate-800">
            <div>
              <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Users</h2>
              <p className="text-sm text-slate-600 dark:text-slate-400">
                A snapshot of every signed up account with their usage.
              </p>
            </div>
            <Badge variant="outline" className="text-slate-600 dark:text-slate-300">
              {numberFormatter.format(users.length)} users
            </Badge>
          </div>

          <div className="overflow-x-auto">
            <Table>
              <TableHeader className="bg-slate-50 dark:bg-slate-950/50">
                <TableRow>
                  <TableHead className="px-6 py-3 text-left font-medium text-slate-600 dark:text-slate-300">
                    ID
                  </TableHead>
                  <TableHead className="px-6 py-3 text-left font-medium text-slate-600 dark:text-slate-300">
                    User
                  </TableHead>
                  <TableHead className="px-6 py-3 text-left font-medium text-slate-600 dark:text-slate-300">
                    Trial
                  </TableHead>
                  <TableHead className="px-6 py-3 text-left font-medium text-slate-600 dark:text-slate-300">
                    Subscription
                  </TableHead>
                  <TableHead className="px-6 py-3 text-left font-medium text-slate-600 dark:text-slate-300">
                    Domains
                  </TableHead>
                  <TableHead className="px-6 py-3 text-left font-medium text-slate-600 dark:text-slate-300">
                    Images
                  </TableHead>
                  <TableHead className="px-6 py-3 text-left font-medium text-slate-600 dark:text-slate-300">
                    Actions
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map((user) => {
                  const trialBadge = trialBadgeVariant(user.trialStatus);
                  const subscriptionBadge = subscriptionBadgeVariant(user.subscriptionStatus);
                  const sourceBadge = sourceBadgeVariant(user.source);
                  const trialDateLabel = user.trialEndsAt
                    ? `${user.trialStatus === "expired" ? "Ended" : "Ends"} ${dateFormatter.format(
                        new Date(user.trialEndsAt),
                      )}`
                    : "No trial window";

                  return (
                    <TableRow key={user.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/50">
                      <TableCell className="px-6 py-4 align-top">
                        <div className="text-sm text-slate-600 dark:text-slate-400">{user.id}</div>
                      </TableCell>
                      <TableCell className="px-6 py-4 align-top">
                        <div className="font-semibold text-slate-900 dark:text-slate-100">{user.name}</div>
                        <div className="text-sm text-slate-600 dark:text-slate-400">{user.email}</div>
                        <div className="mt-1 text-xs text-slate-500 dark:text-slate-500">
                          Signed up {dateFormatter.format(new Date(user.createdAt))}
                        </div>
                        <div className="mt-2">
                          <Badge variant={sourceBadge.variant}>{sourceBadge.label}</Badge>
                        </div>
                      </TableCell>
                      <TableCell className="px-6 py-4 align-top">
                        <Badge variant={trialBadge.variant}>{trialBadge.label}</Badge>
                        <div className="mt-1 text-xs text-slate-500 dark:text-slate-400">{trialDateLabel}</div>
                      </TableCell>
                      <TableCell className="px-6 py-4 align-top">
                        <div className="flex flex-wrap items-center gap-2">
                          <Badge variant={subscriptionBadge.variant}>{subscriptionBadge.label}</Badge>
                          <span className="text-sm font-medium text-slate-800 dark:text-slate-200">
                            {formatPlanName(user.subscriptionPlan)}
                          </span>
                        </div>
                        <div className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                          {user.subscriptionPlan ? "Plan applied" : "No plan on record"}
                        </div>
                      </TableCell>
                      <TableCell className="px-6 py-4 align-top">
                        {user.domains.length > 0 ? (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <div className="cursor-help text-lg font-semibold text-slate-900 dark:text-slate-50">
                                {numberFormatter.format(user.domainsCount)}
                              </div>
                            </TooltipTrigger>
                            <TooltipContent className="max-w-xs" side="top" align="start">
                              <div className="flex flex-col gap-1">
                                <div className="font-medium">Domains:</div>
                                <div className="flex flex-col gap-0.5">
                                  {user.domains.map((hostname, idx) => (
                                    <div key={idx} className="text-xs">
                                      {hostname}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            </TooltipContent>
                          </Tooltip>
                        ) : (
                          <div className="text-lg font-semibold text-slate-900 dark:text-slate-50">
                            {numberFormatter.format(user.domainsCount)}
                          </div>
                        )}
                        <div className="text-xs text-slate-500 dark:text-slate-400">Added domains</div>
                      </TableCell>
                      <TableCell className="px-6 py-4 align-top">
                        <button
                          type="button"
                          onClick={() => handleViewSocialImages(user.id)}
                          className="text-lg font-semibold text-slate-900 transition-colors hover:text-blue-600 focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 focus-visible:outline-none dark:text-slate-50 dark:hover:text-blue-300 dark:focus-visible:ring-offset-slate-900"
                        >
                          {numberFormatter.format(user.imagesCount)}
                        </button>
                        <div className="text-xs text-slate-500 dark:text-slate-400">Images generated</div>
                      </TableCell>
                      <TableCell className="px-6 py-4 align-top">
                        <div className="flex flex-wrap items-center gap-2">
                          <Button type="button" variant="outline" size="sm" onClick={() => openTrialDialog(user)}>
                            Extend trial
                          </Button>
                          <Button
                            type="button"
                            variant="destructive"
                            size="sm"
                            disabled={deletingUserId === user.id}
                            onClick={() => handleDeleteUser(user)}
                          >
                            {deletingUserId === user.id ? "Deleting..." : "Delete"}
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
                {users.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} className="px-6 py-8 text-center text-sm text-slate-500 dark:text-slate-400">
                      No users found.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </div>
      </div>

      <Dialog open={trialDialogOpen} onOpenChange={handleTrialDialogChange}>
        <DialogContent className="sm:max-w-[420px]">
          <form onSubmit={handleExtendTrial} className="space-y-4">
            <DialogHeader>
              <DialogTitle>Extend trial</DialogTitle>
              <DialogDescription>
                {selectedUser
                  ? `Set a new trial end date for ${selectedUser.name}.`
                  : "Set a new trial end date for this user."}{" "}
                Dates must be in the future.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-2">
              <Label htmlFor="trial-end-date">New trial end date</Label>
              <Input
                id="trial-end-date"
                type="date"
                min={minimumTrialDate}
                value={trialEndDate}
                onChange={(event) => setTrialEndDate(event.target.value)}
                required
              />
              <p className="text-xs text-slate-500 dark:text-slate-400">Choose a date after {minimumTrialDateLabel}.</p>
              {selectedUser?.trialEndsAt && (
                <p className="text-xs text-slate-500 dark:text-slate-400">
                  Current trial ends {dateFormatter.format(new Date(selectedUser.trialEndsAt))}.
                </p>
              )}
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={closeTrialDialog}>
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={!selectedUser || !trialEndDate || trialEndDate < minimumTrialDate || isSubmittingTrial}
              >
                Extend trial
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}
